/**
 * Prompt Templates API
 * API client for prompt template management
 */

import apiClient from './client';
import type {
  PromptTemplate,
  PromptSection,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateListResponse,
  SectionOrderItem,
  LivePreviewRequest,
  LivePreviewResponse,
} from '@/types/promptTemplates';

export const promptTemplatesApi = {
  /**
   * List all templates
   */
  list: async (includeInactive = false): Promise<PromptTemplate[]> => {
    const response = await apiClient.get<TemplateListResponse>('/prompt-templates', {
      params: { include_inactive: includeInactive },
    });
    return response.data.templates;
  },

  /**
   * Get a single template by ID
   */
  get: async (templateId: number): Promise<PromptTemplate> => {
    const response = await apiClient.get<PromptTemplate>(`/prompt-templates/${templateId}`);
    return response.data;
  },

  /**
   * Get the default template
   */
  getDefault: async (): Promise<PromptTemplate> => {
    const response = await apiClient.get<PromptTemplate>('/prompt-templates/default');
    return response.data;
  },

  /**
   * Get available section types
   */
  getAvailableSections: async (): Promise<PromptSection[]> => {
    const response = await apiClient.get<PromptSection[]>('/prompt-templates/sections/available');
    return response.data;
  },

  /**
   * Create a new template
   */
  create: async (data: CreateTemplateRequest): Promise<PromptTemplate> => {
    const response = await apiClient.post<PromptTemplate>('/prompt-templates', data);
    return response.data;
  },

  /**
   * Update a template
   */
  update: async (templateId: number, data: UpdateTemplateRequest): Promise<PromptTemplate> => {
    const response = await apiClient.put<PromptTemplate>(`/prompt-templates/${templateId}`, data);
    return response.data;
  },

  /**
   * Delete a template (soft delete)
   */
  delete: async (templateId: number): Promise<void> => {
    await apiClient.delete(`/prompt-templates/${templateId}`);
  },

  /**
   * Set a template as default
   */
  setDefault: async (templateId: number): Promise<PromptTemplate> => {
    const response = await apiClient.post<PromptTemplate>(`/prompt-templates/${templateId}/set-default`);
    return response.data;
  },

  /**
   * Live preview with real agent context and all hooks execution
   * This creates a real agent context, runs all hooks, and returns the final prompt
   */
  livePreview: async (request: LivePreviewRequest): Promise<LivePreviewResponse> => {
    const response = await apiClient.post<LivePreviewResponse>('/prompt-templates/live-preview', request);
    return response.data;
  },

  /**
   * Add a section to a template
   */
  addSection: async (templateId: number, section: PromptSection): Promise<PromptTemplate> => {
    const response = await apiClient.post<PromptTemplate>(
      `/prompt-templates/${templateId}/sections`,
      section
    );
    return response.data;
  },

  /**
   * Update a section in a template
   */
  updateSection: async (
    templateId: number,
    sectionId: string,
    section: PromptSection
  ): Promise<PromptTemplate> => {
    const response = await apiClient.put<PromptTemplate>(
      `/prompt-templates/${templateId}/sections/${sectionId}`,
      section
    );
    return response.data;
  },

  /**
   * Delete a section from a template
   */
  deleteSection: async (templateId: number, sectionId: string): Promise<PromptTemplate> => {
    const response = await apiClient.delete<PromptTemplate>(
      `/prompt-templates/${templateId}/sections/${sectionId}`
    );
    return response.data;
  },

  /**
   * Reorder sections in a template
   */
  reorderSections: async (templateId: number, orders: SectionOrderItem[]): Promise<PromptTemplate> => {
    const response = await apiClient.post<PromptTemplate>(
      `/prompt-templates/${templateId}/sections/reorder`,
      orders
    );
    return response.data;
  },
};
